import { useMemo, useState } from "react";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import TopicCard from "@/components/feature/TopicCard";
import CtaBand from "@/components/feature/CtaBand";
import { useSeo } from "@/hooks/useSeo";
import { topics } from "@/mocks/topics";
import TopicsFilter from "./components/TopicsFilter";

const jsonLd = [
  {
    "@context": "https://schema.org",
    "@type": "CollectionPage",
    name: "高热度选题参考库",
    description:
      "正在征集的高热度爆料投稿选题，按民生现场、消费维权、行业职场、科技互联网、城市生活与文化娱乐分类整理。",
    url: "https://sxnxp.cn/topics",
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "首页", item: "https://sxnxp.cn/" },
      { "@type": "ListItem", position: 2, name: "选题库", item: "https://sxnxp.cn/topics" },
    ],
  },
  {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: "高热度投稿选题列表",
    itemListElement: topics.map((topic, index) => ({
      "@type": "ListItem",
      position: index + 1,
      name: topic.title,
    })),
  },
];

export default function Topics() {
  const [category, setCategory] = useState("全部选题");
  const [keyword, setKeyword] = useState("");
  const [sort, setSort] = useState("热度优先");

  useSeo({
    title: "高热度选题参考库 - 正在征集的爆料投稿方向 | 爆点投稿社",
    description:
      "整理民生现场、消费维权、行业职场、科技互联网等正在征集的高热度选题，附可切入角度与标题参考，帮你快速找到容易被采用的爆料投稿方向。",
    keywords: "高热度选题参考,爆料投稿,选题库,征集话题,编辑偏好方向",
    path: "/topics",
    jsonLd,
  });

  const filtered = useMemo(() => {
    const list = topics.filter((topic) => {
      const matchCategory = category === "全部选题" || topic.category === category;
      const kw = keyword.trim();
      const matchKeyword =
        kw.length === 0 ||
        topic.title.includes(kw) ||
        topic.summary.includes(kw) ||
        topic.category.includes(kw) ||
        topic.angles.some((angle) => angle.includes(kw));
      return matchCategory && matchKeyword;
    });

    if (sort === "热度优先") return [...list].sort((a, b) => b.heatScore - a.heatScore);
    if (sort === "线索最多") return [...list].sort((a, b) => b.leads - a.leads);
    return list;
  }, [category, keyword, sort]);

  return (
    <>
      <section className="w-full border-b border-background-200 bg-background-100 px-4 pb-10 pt-8 md:px-6 md:pb-14 md:pt-10">
        <div className="mx-auto w-full">
          <Breadcrumbs items={[{ label: "选题库" }]} />
          <div className="mt-6 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:items-end">
            <div className="lg:col-span-8">
              <p className="inline-flex items-center gap-2 text-xs font-medium tracking-[0.2em] uppercase text-primary-600">
                <span className="h-px w-6 bg-primary-400"></span>
                选题弹药库
              </p>
              <h1 className="mt-4 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
                高热度选题参考
              </h1>
              <p className="mt-4 max-w-2xl text-sm leading-relaxed text-foreground-600 md:text-base">
                这里收录编辑正在征集的方向。每条选题都标注了热度、截止时间与可切入角度，
                并给出标题参考。挑一条你手上有材料、够得着的方向，比盲目跟热点更容易过稿。
              </p>
            </div>
            <div className="lg:col-span-4">
              <dl className="grid grid-cols-2 gap-4 rounded-lg border border-background-200 bg-background-50 p-5">
                <div>
                  <dt className="text-xs text-foreground-500">在征选题</dt>
                  <dd className="mt-1 font-heading text-2xl font-bold text-foreground-950">
                    {topics.length}
                  </dd>
                </div>
                <div>
                  <dt className="text-xs text-foreground-500">高热方向</dt>
                  <dd className="mt-1 font-heading text-2xl font-bold text-primary-600">
                    {topics.filter((t) => t.heat === "高热").length}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      </section>

      <section className="w-full bg-background-50 px-4 py-12 md:px-6 md:py-16">
        <div className="mx-auto w-full">
          <TopicsFilter
            activeCategory={category}
            onCategoryChange={setCategory}
            keyword={keyword}
            onKeywordChange={setKeyword}
            sort={sort}
            onSortChange={setSort}
            resultCount={filtered.length}
          />

          {filtered.length > 0 ? (
            <div className="mt-8 grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
              {filtered.map((topic) => (
                <TopicCard key={topic.id} topic={topic} />
              ))}
            </div>
          ) : (
            <div className="mt-10 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-search-eye-line text-2xl leading-none"></i>
              </div>
              <h2 className="mt-5 font-heading text-lg font-bold text-foreground-900">
                没有匹配的选题
              </h2>
              <p className="mt-2 max-w-sm text-sm text-foreground-600">
                试试更换分类或缩短关键词。也可以直接投稿你手上的线索，编辑会帮你判断是否值得跟进。
              </p>
              <button
                type="button"
                onClick={() => {
                  setCategory("全部选题");
                  setKeyword("");
                }}
                className="mt-6 inline-flex items-center gap-2 rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-refresh-line text-base leading-none"></i>
                重置筛选条件
              </button>
            </div>
          )}
        </div>
      </section>

      <section className="w-full bg-background-100 px-4 py-14 md:px-6 md:py-20">
        <div className="mx-auto grid w-full grid-cols-1 gap-6 md:grid-cols-3">
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex h-11 w-11 items-center justify-center rounded-md bg-primary-100 text-primary-600">
              <i className="ri-focus-3-line text-xl leading-none"></i>
            </div>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">先选够得着的</h2>
            <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
              热搜上的大选题往往需要专业采访能力。从你身边、你行业内的事切入，反而更容易拿出独家信息。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex h-11 w-11 items-center justify-center rounded-md bg-accent-100 text-accent-700">
              <i className="ri-stack-line text-xl leading-none"></i>
            </div>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">看截止时间再动手</h2>
            <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
              标注「本周内优先」的选题节奏更快，材料齐了就别拖。长期征集的选题适合慢慢补齐证据链。
            </p>
          </div>
          <div className="rounded-lg border border-background-200 bg-background-50 p-6">
            <div className="flex h-11 w-11 items-center justify-center rounded-md bg-secondary-100 text-secondary-700">
              <i className="ri-links-line text-xl leading-none"></i>
            </div>
            <h2 className="mt-4 font-heading text-lg font-bold text-foreground-950">一条线索多点切入</h2>
            <p className="mt-2.5 text-sm leading-relaxed text-foreground-600">
              同一件事可以从当事人、从业者、监管方三个角度写。先投一条，编辑觉得有价值会跟你一起拓展。
            </p>
          </div>
        </div>
      </section>

      <CtaBand
        title="看中了某个方向，就把它写下来"
        desc="不用等材料完美无缺，先把你知道的部分提交，编辑会告诉你还缺什么。"
        secondaryLabel="学习投稿技巧"
        secondaryTo="/guide"
      />
    </>
  );
}